// Front-end for the Help Centre (Pages/Help/Index.cshtml). Three jobs:
//   1. Role tabs — clicking one shows its .help-panel (CSS toggles display + a
//      fade-in; inactive panels are display:none, so out of the a11y tree). The
//      tab row also gets edge scroll arrows on tiny phones (see below).
//   2. Search — the PC suggestions popup and (below sm) the full-screen overlay
//      both match the backend-served /help.json index client-side. The shared
//      helpers (and the overlay itself, reused by the article, Careers and
//      Feedback pages) live in Assets/helpSearch.ts. Each result links to its
//      /help/<slug> article.
//   3. Scroll-reveal on the always-visible "explore more" closer, matching the
//      rest of the site. Peer Vite entry — never imports the SCSS.

import { debounce } from 'lodash-es';
import { revealOnScroll } from '../../Assets/scrollReveal';
import { renderHelpResults, renderHelpPopular, initHelpSearchOverlay } from '../../Assets/helpSearch';

// ── Role tabs ────────────────────────────────────────────────────────────────
const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>('.help-tabs .help-tab'));
const panels = Array.from(document.querySelectorAll<HTMLElement>('.help-panel'));

function selectTab(tab: HTMLButtonElement): void {
    const target = tab.dataset.panel;
    tabs.forEach((t) => {
        const on = t === tab;
        t.classList.toggle('active', on);
        t.setAttribute('aria-selected', String(on));
    });
    panels.forEach((p) => p.classList.toggle('active', p.dataset.panel === target));
}

tabs.forEach((tab) => tab.addEventListener('click', () => selectTab(tab)));

// Tab-bar scroll affordances — show an edge chevron whenever the track can
// scroll that way (real overflow, not a breakpoint), and nudge it on click.
const tabBar = document.querySelector<HTMLElement>('.help-tabs');
const tabTrack = tabBar?.querySelector<HTMLElement>('.help-tabs-track');

if (tabBar && tabTrack) {
    const track = tabTrack; // narrowed (non-null) for the closures below

    const updateArrows = (): void => {
        const { scrollLeft, scrollWidth, clientWidth } = track;
        tabBar.classList.toggle('can-start', scrollLeft > 1);
        tabBar.classList.toggle('can-end', scrollLeft + clientWidth < scrollWidth - 1);
    };

    tabBar.querySelectorAll<HTMLButtonElement>('.help-tabs-arrow').forEach((arrow) => {
        const direction = arrow.classList.contains('start') ? -1 : 1;
        arrow.addEventListener('click', () => {
            track.scrollBy({ left: direction * track.clientWidth * 0.7, behavior: 'smooth' });
        });
    });

    track.addEventListener('scroll', updateArrows, { passive: true });
    // Debounced like every other resize handler here (Carousel.ts, Careers.cshtml.ts):
    // updateArrows reads scrollLeft/scrollWidth/clientWidth then writes two classes, so
    // running it raw forces a layout on every event of a drag-resize.
    window.addEventListener('resize', debounce(updateArrows, 150));
    // Once the web fonts land — label widths (and thus overflow / tab offsets)
    // shift — recompute the arrows AND bring a deep-linked active tab into view:
    // ?tab= can start the active tab off-screen in the scroll track. Horizontal
    // only (block: 'nearest') so the page never jumps; skip when it's already the
    // first tab. The scroll itself fires updateArrows via the track listener.
    void document.fonts.ready.then(() => {
        updateArrows();
        const activeTab = track.querySelector<HTMLElement>('.help-tab.active');
        if (activeTab && activeTab !== track.firstElementChild) {
            activeTab.scrollIntoView({ inline: 'center', block: 'nearest' });
        }
    });
    updateArrows();
}

// ── PC search — suggestions popup ────────────────────────────────────────────
const searchInput = document.querySelector<HTMLInputElement>('.help-search input');
const suggestPopular = document.querySelector<HTMLElement>('.help-suggest .suggest-popular');
const suggestResults = document.querySelector<HTMLElement>('.help-suggest .suggest-results');

// Fill the popup's popular block (priority-0 articles) up front.
renderHelpPopular(document.querySelector<HTMLElement>('.help-suggest .suggest-popular-list'), 'suggest-item');

if (searchInput) {
    const render = (): void =>
        renderHelpResults(searchInput.value, suggestPopular, suggestResults, 'suggest-item', 'suggest-empty', () => searchInput.value);
    searchInput.addEventListener('input', render);
    // Live results — never let Enter submit/navigate the search form.
    searchInput.closest('form')?.addEventListener('submit', (event) => {
        event.preventDefault();
        render();
    });
}

// ── Mobile full-screen search overlay ────────────────────────────────────────
// Below sm the grey pill acts as a button: tapping it opens the shared overlay
// (Assets/helpSearch.ts) instead of focusing inline.
const overlay = initHelpSearchOverlay();

if (overlay && searchInput) {
    // Intercept the inline pill on mobile — preventDefault on mousedown stops the
    // inline input focusing (no soft keyboard), then open the overlay instead.
    // isFullScreen() reads the overlay's own computed display, so the width threshold lives
    // once in HelpSearchOverlay.cshtml.scss (media-breakpoint-up(sm)) instead of being
    // duplicated here as a literal that could silently drift from the tier map.
    searchInput.closest('form')?.addEventListener('mousedown', (event) => {
        if (overlay.isFullScreen()) {
            event.preventDefault();
            overlay.open();
        }
    });
}

// Scroll-reveal the Explore closer (revealOnScroll itself no-ops in embedded mode).
revealOnScroll('.help-explore .block-title, .help-explore .explore-card, .help-explore .contact-card');
